import { sessionsPath } from "~/common/paths"
import { useDocumentTitle } from "~/common/use-document-title"
import type { AssetId } from "~/content/assets"
import { ResponsiveImage } from "~/content/responsive-image"
import { ScheduleLink } from "~/marketing/schedule-link"

const outcomes = [
  ["A project of your own", "Work on an app, an internal tool, or a workflow that matters to you."],
  [
    "A way to keep building",
    "Practice shaping an idea, guiding AI, and checking what it produces.",
  ],
  [
    "Help when you get stuck",
    "Dave, Eric, and the team work alongside participants throughout the event.",
  ],
] as const

const steps = [
  ["Before the event", "We learn about your group and help participants get their tools ready."],
  [
    "Learn and choose",
    "Start with a practical introduction. Turn an idea into a focused build plan.",
  ],
  [
    "Build with support",
    "Work on your project with help on prompts, product decisions, and technical problems.",
  ],
  ["Test and share", "Try what you built, show the group, and decide what comes next."],
] as const

const gallery: readonly { assetId: AssetId; alt: string }[] = [
  {
    assetId: "hackathon-eric-room",
    alt: "Eric Skiff checks his phone while participants work at laptops around the room",
  },
  {
    assetId: "hackathon-building",
    alt: "Participants building on their laptops at long tables",
  },
  {
    assetId: "hackathon-demo",
    alt: "A participant presents their project on the big screen",
  },
]

const ctaClassName =
  "inline-flex min-h-12 w-fit items-center rounded-full bg-primary px-8 text-lg font-semibold text-primary-foreground hover:bg-primary/90"

const FeatureList = ({
  items,
  dark = false,
}: {
  items: readonly (readonly [string, string])[]
  dark?: boolean
}) => (
  <ul className="mt-10 grid gap-8">
    {items.map(([title, body]) => (
      <li key={title}>
        <h3 className="text-2xl font-bold tracking-[-0.01em]">{title}</h3>
        <p
          className={
            dark
              ? "mt-2 text-lg leading-relaxed text-secondary-foreground/75"
              : "mt-2 text-lg leading-relaxed text-muted-foreground"
          }
        >
          {body}
        </p>
      </li>
    ))}
  </ul>
)

export const HackathonsPage = () => {
  useDocumentTitle("AI workshops & hackathons | Tanooki Labs")
  return (
    <>
      <section className="container marketing-hero grid gap-10 md:grid-cols-2 md:items-center lg:gap-20">
        <div className="max-w-xl">
          <p className="marketing-eyebrow marketing-orange-text">AI workshops &amp; hackathons</p>
          <h1 className="home-sans-heading marketing-page-title marketing-split-title mt-4">
            <span className="headline-beat italic">Get your people</span>
            <span className="headline-beat">building.</span>
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground sm:text-xl">
            Bring your team, founders, or community together to build with AI. We help people get
            set up, choose something worth making, and work through it with experienced developers
            alongside them.
          </p>
          <p className="mt-6 text-sm text-muted-foreground">
            For teams, accelerators, and founder communities
          </p>
          <ScheduleLink ctaLocation="hackathons-hero" className={`${ctaClassName} mt-8`}>
            Plan a hackathon
          </ScheduleLink>
        </div>
        <ResponsiveImage
          assetId="hackathon-group"
          alt="Hackathon participants and the Tanooki Labs team pose together in front of the presentation screen"
          sizes="half"
          priority
          className="aspect-[3/2] w-full rounded-2xl object-cover"
        />
      </section>
      <section className="marketing-surface-dark marketing-section text-secondary-foreground">
        <div className="container">
          <h2 className="home-sans-heading marketing-section-heading">
            An idea becomes something you can try.
          </h2>
          <FeatureList items={outcomes} dark />
        </div>
      </section>
      <section className="container marketing-section">
        <h2 className="home-sans-heading marketing-section-heading">
          From the first prompt to the demo.
        </h2>
        <FeatureList items={steps} />
      </section>
      <ResponsiveImage
        assetId="hackathon-room-wide"
        alt="A full room of participants working on their projects"
        className="aspect-[16/9] w-full object-cover sm:aspect-[21/9]"
      />
      <section className="marketing-surface-dark marketing-section text-secondary-foreground">
        <div className="container">
          <h2 className="home-sans-heading marketing-section-heading">Built around your group.</h2>
          <p className="mt-6 max-w-4xl text-lg leading-relaxed text-secondary-foreground/75 sm:text-xl">
            Our previous creator hackathons paired introductory sessions with project planning,
            supported build time, testing, and demos. We’ll adapt that approach to your team’s
            experience and goals.
          </p>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {gallery.map(({ assetId, alt }) => (
              <ResponsiveImage
                key={assetId}
                assetId={assetId}
                alt={alt}
                sizes="third"
                className="aspect-[4/3] w-full rounded-2xl object-cover"
              />
            ))}
          </div>
        </div>
      </section>
      <section className="container marketing-section home-closing-grid">
        <div>
          <h2 className="home-sans-heading marketing-section-heading">
            <span className="italic">Let’s plan your hackathon.</span>
          </h2>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
            Tell us who’s coming, what they want to learn, and the time you have. We’ll shape the
            event around your group.
          </p>
          <ScheduleLink ctaLocation="hackathons-closing" className={`${ctaClassName} mt-8`}>
            Plan a hackathon
          </ScheduleLink>
          <p className="mt-6 text-muted-foreground">
            Prefer to start with a group session?{" "}
            <a href={sessionsPath({})} className="font-semibold text-foreground hover:underline">
              Join the free webinar →
            </a>
          </p>
        </div>
        <ResponsiveImage
          assetId="hackathon-winners"
          alt="Four hackathon participants smiling together, one holding a Mac mini prize"
          sizes="half"
          className="aspect-[4/3] w-full rounded-2xl object-cover"
        />
      </section>
    </>
  )
}
